import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { BulkActions } from '@/components/BulkActions';
import {
  CorrelationResult,
  DataQualityReport,
  Dataset,
  ExportOptions,
  ExportResult,
} from '@/lib/types';
import {
  CalendarIcon,
  CaretDownIcon,
  CaretUpIcon,
  ChartBarIcon,
  DatabaseIcon,
  EyeIcon,
  FileTextIcon,
  InfoIcon,
  XIcon,
} from '@phosphor-icons/react';

interface SelectedDatasetsPanelProps {
  readonly datasets: readonly Dataset[];
  readonly onRemoveDataset: (datasetId: string) => void;
  readonly onClearAll: () => void;
  readonly onPreviewDataset: (dataset: Dataset) => void;
  readonly isExpanded: boolean;
  readonly onToggleExpand: () => void;
  readonly qualityReports: Readonly<Record<string, DataQualityReport>>;
  readonly onQualityReportsUpdate: (
    reports: Record<string, DataQualityReport>
  ) => void;
  readonly onExport: (options: ExportOptions) => Promise<ExportResult>;
  readonly onCorrelationAnalysis?: (correlations: CorrelationResult[]) => void;
}

export function SelectedDatasetsPanel({
  datasets,
  onRemoveDataset,
  onClearAll,
  onPreviewDataset,
  isExpanded,
  onToggleExpand,
  qualityReports,
  onQualityReportsUpdate,
  onExport,
  onCorrelationAnalysis,
}: Readonly<SelectedDatasetsPanelProps>) {
  const [expandedDataset, setExpandedDataset] = useState<string | null>(null);

  // Auto-collapse expanded dataset schema when panel is collapsed
  useEffect(() => {
    if (!isExpanded) {
      setExpandedDataset(null);
    }
  }, [isExpanded]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts when panel is visible
      if (datasets.length === 0) {
        return;
      }

      if (event.key === 'Escape' && expandedDataset) {
        setExpandedDataset(null);
        event.preventDefault();
      }

      if (event.key === 'Enter' && event.altKey) {
        onToggleExpand();
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [datasets.length, expandedDataset, onToggleExpand]);

  const totalRows = datasets.reduce(
    (sum, dataset) => sum + dataset.totalRows,
    0
  );
  const totalFields = datasets.reduce(
    (sum, dataset) => sum + dataset.schema.length,
    0
  );
  const avgQuality =
    datasets.length > 0
      ? datasets.reduce((sum, dataset) => sum + dataset.qualityScore, 0) /
        datasets.length
      : 0;

  const getQualityColor = (score: number) => {
    if (score >= 80) {
      return 'text-quality-good';
    }
    if (score >= 60) {
      return 'text-quality-warning';
    }

    return 'text-quality-poor';
  };

  const getQualityBadgeVariant = (score: number) => {
    if (score >= 80) {
      return 'default';
    }
    if (score >= 60) {
      return 'secondary';
    }

    return 'destructive';
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }

    return num.toLocaleString();
  };

  return (
    <div className='border-b bg-muted/20'>
      <div className='container mx-auto px-4 sm:px-6 py-4 max-w-full'>
        <Collapsible open={isExpanded} onOpenChange={onToggleExpand}>
          <div className='flex items-center justify-between min-w-0 gap-2 sm:gap-4'>
            <CollapsibleTrigger asChild>
              <div className='flex items-center gap-2 group cursor-pointer min-w-0 flex-shrink hover:text-primary transition-colors'>
                <DatabaseIcon className='w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0' />
                <h2 className='text-base sm:text-lg font-semibold truncate'>
                  Selected Datasets ({datasets.length})
                </h2>
                {isExpanded ? (
                  <CaretUpIcon className='w-4 h-4 text-muted-foreground' />
                ) : (
                  <CaretDownIcon className='w-4 h-4 text-muted-foreground' />
                )}
                <span className='hidden lg:inline text-xs text-muted-foreground bg-muted px-2 py-1 rounded'>
                  Alt+Enter to toggle
                </span>
              </div>
            </CollapsibleTrigger>

            <div className='flex items-center gap-2'>
              {datasets.length > 0 && (
                <>
                  <div className='hidden sm:flex items-center gap-4 text-xs text-muted-foreground'>
                    <span className='flex items-center gap-1'>
                      <ChartBarIcon className='w-3 h-3' />
                      {formatNumber(totalRows)} rows
                    </span>
                    <span className='flex items-center gap-1'>
                      <FileTextIcon className='w-3 h-3' />
                      {totalFields} fields
                    </span>
                    <span
                      className={`flex items-center gap-1 ${getQualityColor(avgQuality)}`}
                    >
                      <InfoIcon className='w-3 h-3' />
                      {avgQuality.toFixed(0)}% avg quality
                    </span>
                  </div>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={e => {
                      e.stopPropagation();
                      onClearAll();
                    }}
                    className='gap-1 flex-shrink-0'
                  >
                    <XIcon className='w-3 h-3' />
                    <span className='hidden sm:inline'>Clear All</span>
                  </Button>
                </>
              )}
            </div>
          </div>

          <CollapsibleContent className='mt-4'>
            {datasets.length > 0 ? (
              <div className='space-y-4'>
                {/* Bulk Actions */}
                <BulkActions
                  datasets={[...datasets]}
                  qualityReports={qualityReports}
                  onQualityReportsUpdate={onQualityReportsUpdate}
                  onExport={onExport}
                  onCorrelationAnalysis={onCorrelationAnalysis}
                />

                {/* Summary Stats */}
                <div className='grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-card rounded-lg border'>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-primary'>
                      {datasets.length}
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      Datasets
                    </div>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-foreground'>
                      {formatNumber(totalRows)}
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      Total Rows
                    </div>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-foreground'>
                      {totalFields}
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      Total Fields
                    </div>
                  </div>
                  <div className='text-center'>
                    <div
                      className={`text-2xl font-bold ${getQualityColor(avgQuality)}`}
                    >
                      {avgQuality.toFixed(0)}%
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      Avg Quality
                    </div>
                  </div>
                </div>

                {/* Dataset List */}
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                  {datasets.map(dataset => (
                    <Card key={dataset.id} className='relative'>
                      <CardHeader className='pb-3'>
                        <div className='flex items-start justify-between gap-2'>
                          <div className='min-w-0 flex-1'>
                            <CardTitle
                              className='text-sm font-medium truncate'
                              title={dataset.name}
                            >
                              {dataset.name}
                            </CardTitle>
                            <CardDescription className='text-xs line-clamp-2 mt-1'>
                              {dataset.description}
                            </CardDescription>
                          </div>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => onRemoveDataset(dataset.id)}
                            className='h-8 w-8 p-0 text-muted-foreground hover:text-destructive flex-shrink-0'
                            title='Remove dataset'
                          >
                            <XIcon className='w-3 h-3' />
                          </Button>
                        </div>
                      </CardHeader>

                      <CardContent className='pt-0 space-y-3'>
                        {/* Dataset Stats */}
                        <div className='grid grid-cols-2 gap-2 text-xs'>
                          <div className='flex items-center gap-1'>
                            <ChartBarIcon className='w-3 h-3 text-muted-foreground' />
                            <span>{formatNumber(dataset.totalRows)} rows</span>
                          </div>
                          <div className='flex items-center gap-1'>
                            <FileTextIcon className='w-3 h-3 text-muted-foreground' />
                            <span>{dataset.schema.length} fields</span>
                          </div>
                          <div className='flex items-center gap-1'>
                            <CalendarIcon className='w-3 h-3 text-muted-foreground' />
                            <span>{dataset.lastUpdated}</span>
                          </div>
                          <div className='flex items-center gap-1'>
                            <Badge
                              variant={getQualityBadgeVariant(
                                dataset.qualityScore
                              )}
                              className='text-xs px-1 py-0'
                            >
                              {dataset.qualityScore}% quality
                            </Badge>
                          </div>
                        </div>

                        {/* Field Types Summary */}
                        <div className='space-y-2'>
                          <div className='text-xs font-medium text-muted-foreground'>
                            Field Types:
                          </div>
                          <div className='flex flex-wrap gap-1'>
                            {(() => {
                              const typeCounts = dataset.schema.reduce(
                                (acc, field) => {
                                  acc[field.type] = (acc[field.type] || 0) + 1;

                                  return acc;
                                },
                                {} as Record<string, number>
                              );

                              return Object.entries(typeCounts).map(
                                ([type, count]) => (
                                  <Badge
                                    key={type}
                                    variant='outline'
                                    className='text-xs px-1 py-0'
                                  >
                                    {count} {type}
                                  </Badge>
                                )
                              );
                            })()}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className='flex gap-2 pt-2'>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => onPreviewDataset(dataset)}
                            className='flex-1 gap-1 text-xs'
                          >
                            <EyeIcon className='w-3 h-3' />
                            Preview
                          </Button>
                          <Collapsible
                            open={expandedDataset === dataset.id}
                            onOpenChange={open =>
                              setExpandedDataset(open ? dataset.id : null)
                            }
                          >
                            <CollapsibleTrigger asChild>
                              <Button
                                variant='outline'
                                size='sm'
                                className='gap-1 text-xs'
                              >
                                {expandedDataset === dataset.id ? (
                                  <CaretUpIcon className='w-3 h-3' />
                                ) : (
                                  <CaretDownIcon className='w-3 h-3' />
                                )}
                                Schema
                              </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent className='mt-3'>
                              <div className='border rounded-md overflow-hidden'>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className='text-xs px-2 py-1'>
                                        Field
                                      </TableHead>
                                      <TableHead className='text-xs px-2 py-1'>
                                        Type
                                      </TableHead>
                                      <TableHead className='text-xs px-2 py-1'>
                                        Nullable
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {dataset.schema.slice(0, 5).map(field => (
                                      <TableRow key={field.name}>
                                        <TableCell className='font-mono text-xs px-2 py-1 truncate max-w-20'>
                                          {field.name}
                                        </TableCell>
                                        <TableCell className='px-2 py-1'>
                                          <Badge
                                            variant='outline'
                                            className='text-xs px-1'
                                          >
                                            {field.type}
                                          </Badge>
                                        </TableCell>
                                        <TableCell className='text-xs px-2 py-1'>
                                          {field.nullable ? 'Yes' : 'No'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                                {dataset.schema.length > 5 && (
                                  <div className='text-xs text-muted-foreground text-center py-2 border-t'>
                                    +{dataset.schema.length - 5} more fields
                                    (click Preview for full schema)
                                  </div>
                                )}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <div className='text-center py-8 text-muted-foreground'>
                <DatabaseIcon className='w-12 h-12 mx-auto mb-3 opacity-50' />
                <p className='text-sm'>No datasets selected yet</p>
                <p className='text-xs mt-1'>
                  Choose datasets from the discovery section to get started
                </p>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
}
